import { getPricingDuration } from "@Pages/Search/components/SearchListItem/helper";
import { getRegionName, getRegionSelectName } from "@Pages/Search/data/helper";
import urlBuilder from "@Utils/urlBuilder";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { getPricingCurrency } from "../components/PriceTrends/helpers";
import isValidMoveInMonth from "@Pages/Search/utils/isValidMoveInMonth";
import {
  WIDENED_MONTH_WINDOW,
  parseMoveInMonthParam,
  widenedMonthParam,
  withStrictMonth,
} from "@Pages/Search/utils/moveInMonthWindow";
import appStyles from "@App/app.module.scss";

const getPropertyLink = (data: any, params = {}) => {
  let url = null;
  if (data?.canonical_name) {
    const paths = [];
    paths.push("/places");
    paths.push(data.canonical_name);

    // add url options
    const url_options: any = {};
    url_options.path = paths;
    url_options.params = params;

    // Build link
    url = urlBuilder(url_options.path, { params: url_options.params });
  }
  return url;
};

export const syncQueryWithURL = (filters: any) => {
  if (window?.location) {
    // @ts-ignore
    const url = new URL(window.location);
    if (filters.meta_min_lease_duration) {
      const minDuration = filters.meta_min_lease_duration;
      url.searchParams.set("meta_min_lease_duration", minDuration);
    } else {
      url.searchParams.delete("meta_min_lease_duration");
    }
    if (filters.meta_max_lease_duration) {
      const maxDuration = filters.meta_max_lease_duration;
      url.searchParams.set("meta_max_lease_duration", maxDuration);
    } else {
      url.searchParams.delete("meta_max_lease_duration");
    }
    const prev = parseMoveInMonthParam(url.searchParams.get("move_in_month"));
    const nextMonth = filters.move_in_month ? formatMoveInMonth(filters.move_in_month) : "";
    if (filters.move_in_month) {
      // A widened month ("nov-2026,oct-2026,dec-2026", P1D-5529) only survives an apply that
      // keeps the same month; a new month is exact.
      const keepWidened = prev.widened && prev.month === nextMonth;
      url.searchParams.set(
        "move_in_month",
        (keepWidened && widenedMonthParam(nextMonth)) || nextMonth,
      );
    } else {
      url.searchParams.delete("move_in_month");
    }

    if (filters?.meta_types && filters?.meta_types?.length) {
      url.searchParams.set("meta_types", filters?.meta_types);
    } else {
      url.searchParams.delete("meta_types");
    }

    if (filters?.tags && filters?.tags?.length) {
      url.searchParams.set("tags", filters?.tags);
    } else {
      url.searchParams.delete("tags");
    }

    return url.toString();
  }
  return "";
};

export const getPlaceMetaTitle = (placeObject: any = {}, userLanguage = "en") => {
  let title = "";

  // Check for translated meta title first
  if (placeObject.meta?.meta_titles_translation?.[userLanguage]?.length > 0) {
    title = placeObject.meta.meta_titles_translation[userLanguage];
  }
  // Fallback to default meta_title
  else if (placeObject.meta?.meta_title?.length > 0) {
    title = placeObject.meta.meta_title;
  }
  // Generate default title
  else {
    const placeName = placeObject.name;
    title = placeName;
    if (placeObject.region) {
      const regionName = getRegionName(placeObject.region);
      if (regionName && !placeName.toLowerCase().includes(regionName.toLowerCase())) {
        title = `${title} in ${regionName}`;
      }
    }
    if (title.length < 28) {
      title += " | Student Housing";
    }
  }
  return title;
};

export const getPlaceMetaKeywords = (placeObject: any = {}) => {
  let keywords = [];
  if (placeObject.name_zh || placeObject.name) {
    keywords.push(placeObject.name_zh || placeObject.name);
  }
  if (placeObject.region) {
    const regionCompleteName = getRegionSelectName(placeObject.region);
    const regionKeywords = regionCompleteName.split(",").map((item) => item.trim());
    keywords = keywords.concat(regionKeywords);
  }
  if (placeObject.meta?.meta_keywords) {
    const metaKeywordsList = placeObject.meta.meta_keywords
      .split(",")
      .map((keyword: any) => keyword.trim());
    keywords = keywords.concat(metaKeywordsList);
  }
  return keywords;
};

export const getPricingPrice = (pricingObject: any) => {
  let price;
  let currency = getPricingCurrency(pricingObject.currency);
  currency = currency || "";
  if (pricingObject?.min_price && pricingObject?.max_price) {
    if (pricingObject?.min_price != pricingObject?.max_price) {
      price = `${
        currency + pricingObject.min_price.toLocaleString("en")
      } - ${currency}${pricingObject.max_price.toLocaleString("en")}`;
    } else {
      price = currency + pricingObject.min_price.toLocaleString("en");
    }
  } else if (pricingObject?.price) {
    price = currency + pricingObject?.price.toLocaleString("en");
  } else if (pricingObject?.min_price) {
    price = `From ${currency}${pricingObject.min_price.toLocaleString("en")}`;
  }
  return price;
};

export const getPlaceMetaDescription = (placeObject: any = {}, userLanguage = "en") => {
  let description = "";

  // Check for translated meta description first
  if (placeObject.meta?.meta_descriptions_translation?.[userLanguage]?.length > 0) {
    description = placeObject.meta.meta_descriptions_translation[userLanguage];
  }
  // Fallback to default meta_description
  else if (placeObject.meta?.meta_description?.length > 0) {
    description = placeObject.meta.meta_description;
  } else {
    const placeName = placeObject.name;
    description = `View details and book ${placeName}`;
    if (placeObject.region) {
      const regionName = getRegionName(placeObject.region);
      const regionCombinedName = getRegionSelectName(placeObject.region, ["country"]);
      if (
        regionName &&
        !placeName.toLowerCase().includes(regionName.toLowerCase()) &&
        regionCombinedName
      ) {
        description = `${description} in ${regionCombinedName}`;
      }
    }
    description += ". ";
    const pricingCurrency = getPricingCurrency(placeObject.pricing.currency);
    const pricingDuration = getPricingDuration(placeObject.pricing.duration);
    const pricingPrice = getPricingPrice(placeObject.pricing);
    if (pricingCurrency && pricingDuration && pricingPrice) {
      description = `${description}Starting ${pricingPrice}/${pricingDuration}. `;
    }
    description += "Get reviews, amenities, photos and promotions. ✓ Verified ✓ Enquire Now!";
  }
  return description;
};

export const getPlaceCanonicalUrl = (placeObject: any = {}, userLanguage = "en") => {
  // Check for translated canonical tag first
  if (placeObject?.meta?.canonical_tags_translation?.[userLanguage]?.length > 0) {
    return placeObject.meta.canonical_tags_translation[userLanguage];
  }

  // Fallback to default canonical_tag
  if (placeObject?.meta?.canonical_tag?.length > 0) {
    return placeObject.meta.canonical_tag;
  }

  // Fall back to default canonical URL generation
  return getPropertyLink(placeObject);
};

export const getPlaceRobotsTag = (placeObject: any = {}) => {
  // Check if inventory has custom meta_robots in meta
  if (placeObject?.meta?.meta_robots?.length > 0) {
    return placeObject.meta.meta_robots;
  }

  // Return null to fall back to default robots tag logic
  return null;
};

export const getLocation = (
  locationObject: any,
  includeOptions = ["locality_long_name", "state_long_name", "country_short_name"],
) => {
  let location = null;
  const location_arr: any = [];
  if (includeOptions.includes("name") && locationObject?.name) {
    location_arr.push(locationObject.name);
  }
  if (includeOptions.includes("street_number") && locationObject?.street_number?.long_name) {
    location_arr.push(locationObject.street_number.long_name);
  }
  if (includeOptions.includes("route_long_name") && locationObject?.route?.long_name) {
    location_arr.push(locationObject.route.long_name);
  }
  if (includeOptions.includes("route_short_name") && locationObject?.route?.short_name) {
    location_arr.push(locationObject.route.short_name);
  }
  if (
    includeOptions.includes("locality_short_name") &&
    (locationObject?.locality?.short_name || locationObject?.district?.short_name)
  ) {
    location_arr.push(locationObject?.locality?.short_name || locationObject?.district?.short_name);
  }
  if (
    includeOptions.includes("locality_long_name") &&
    (locationObject?.locality?.long_name || locationObject?.district?.long_name)
  ) {
    location_arr.push(locationObject?.locality?.long_name || locationObject?.district?.long_name);
  }
  if (includeOptions.includes("postal_code") && locationObject?.postal_code?.long_name) {
    location_arr.push(locationObject.postal_code.long_name);
  }
  if (locationObject?.state?.short_name && includeOptions.includes("state_short_name")) {
    location_arr.push(locationObject.state.short_name);
  }
  if (includeOptions.includes("state_long_name") && locationObject?.state?.long_name) {
    location_arr.push(locationObject.state.long_name);
  }
  if (includeOptions.includes("country_short_name") && locationObject?.country?.short_name) {
    location_arr.push(locationObject.country.short_name);
  }
  if (includeOptions.includes("country_long_name") && locationObject?.country?.long_name) {
    location_arr.push(locationObject.country.long_name);
  }
  if (location_arr.length > 0) {
    location = location_arr.join(", ");
  }
  return location;
};

export const getFiltersFromQuery = (url = "") => {
  const filters: any = {
    meta_min_lease_duration: "",
    meta_max_lease_duration: "",
    move_in_month: "",
    duration: "",
    meta_types: [],
    sharing: [],
    available: [],
    tags: [],
    meta_unit_types: [],
  };

  let urlParams: URLSearchParams;
  if (url) {
    urlParams = new URLSearchParams(String(url).split("?").pop());
  } else {
    urlParams = new URLSearchParams(window.location.search);
  }

  // Specific modification
  const durationStart = urlParams.get("meta_min_lease_duration") || "";
  const durationEnd = urlParams.get("meta_max_lease_duration") || "";
  const meta_types = urlParams.get("meta_types") || "";
  const meta_unit_types = urlParams.get("meta_unit_types") || "";
  const available = urlParams.get("available") || "";
  // "nov-2026,oct-2026,dec-2026" = November widened to ±1 (from a recommended card, P1D-5529).
  const { month: moveInMonth, widened: monthWidened } = parseMoveInMonthParam(
    urlParams.get("move_in_month"),
  );
  const tags = urlParams.get("tags") || "";

  if (durationStart) {
    filters.meta_min_lease_duration = durationStart;
  }

  if (durationEnd) {
    filters.meta_max_lease_duration = durationEnd;
  }

  if (moveInMonth && isValidMoveInMonth(moveInMonth)) {
    filters.move_in_month = moveInMonth;
  }

  if (filters.move_in_month && monthWidened) {
    filters.move_in_month_window = WIDENED_MONTH_WINDOW;
  }

  if (meta_types) {
    const meta_types_array = meta_types.split(",");
    meta_types_array.forEach((meta_type) => {
      // @ts-ignore
      filters.meta_types = [...filters.meta_types, meta_type];
    });
  }

  if (available) {
    // @ts-ignore
    filters.available = [...filters.available, available];
  }

  if (tags) {
    // @ts-ignore
    filters.tags = [...filters.tags, tags];
  }

  if (meta_unit_types) {
    const meta_unit_types_array = meta_unit_types.split(",");
    meta_unit_types_array.forEach((meta_type) => {
      // @ts-ignore
      filters.meta_unit_types = [...filters.meta_unit_types, meta_type];
    });
  }

  Object.keys({ ...filters }).forEach((key: any) => {
    if (!filters[key] || !filters[key].length) {
      delete filters[key];
    }
  });

  return filters;
};

export const formatMoveInMonth = (moveInMonth: any) => {
  /* const monthsList = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
  ]; */
  const splittedDate = moveInMonth.split("-");
  const month = splittedDate[0].toLowerCase().substring(0, 3);

  if (splittedDate.length === 2) {
    return `${month}-${splittedDate[1]}`;
  }
  const year = new Date().getFullYear();

  return `${month}-${year}`;
};

export const getParams = (filters: any) => {
  const params = {
    type: "details", // Helps to fetch children details along with Config
    limit: 200,
    // use_open_search: true,
  };

  if (filters.meta_min_lease_duration) {
    // @ts-ignore
    params.meta_min_lease_duration = filters.meta_min_lease_duration;
  }
  if (filters.meta_max_lease_duration) {
    // @ts-ignore
    params.meta_max_lease_duration = filters.meta_max_lease_duration;
  }
  if (filters.move_in_month) {
    // @ts-ignore
    params.move_in_month = formatMoveInMonth(filters.move_in_month);
    if (filters.move_in_month_window) {
      // @ts-ignore
      params.move_in_month_window = filters.move_in_month_window;
    }
  }
  if (filters.meta_types?.length) {
    // @ts-ignore
    params.meta_types = `${params.meta_types ? `${params.meta_types},` : ""}${filters.meta_types}`;
  }
  if (filters.sharing?.length) {
    // @ts-ignore
    params.meta_types = `${params.meta_types ? `${params.meta_types},` : ""}${filters.sharing}`;
  }
  if (filters.available) {
    // @ts-ignore
    params.available = filters.available;
  }
  if (filters.tags) {
    // @ts-ignore
    params.tags = String(filters.tags);
  }
  if (filters.meta_unit_types) {
    // @ts-ignore
    params.meta_unit_types = filters.meta_unit_types;
  }
  // Room-list API params only: a month without the URL's widened "1" asks for the exact month
  // ("0"). Never written back to the property page URL or its cache key.
  return withStrictMonth(params);
};

const isPageScrollLocked = () => {
  if (typeof document === "undefined") return true;
  const { body, documentElement } = document;
  return (
    Boolean(body.style.top) ||
    body.classList.contains(appStyles.bodyLocked) ||
    body.classList.contains(appStyles.bodyLockedFixed) ||
    body.classList.contains(appStyles.bodyLockedSticky) ||
    documentElement.classList.contains(appStyles.bodyLocked) ||
    documentElement.classList.contains(appStyles.bodyLockedSticky)
  );
};

export const viewRoomsClickHandler = (ctaText = "") => {
  const element = document.getElementById("room_type");
  if (!element) return;
  const headerOffset = 54 + 48; // padding plus navbar height
  const elementPosition = element?.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

  window.scrollTo({
    top: offsetPosition,
    behavior: "smooth",
  });

  gtagEventEmitter({
    action: "clicked_view_room",
    category: PAGE.INVENTORY,
    label: "success",
    cta_text: ctaText,
  });
};

/**
 * Scroll to View Rooms after the lead-form modal has released its body lock.
 * Immediate scroll is a no-op (or is undone) while the page is `position:fixed`
 * / `overflow:clip`, and mobile `useModal` goBack can restore the pre-open offset.
 */
export const scheduleViewRoomsScroll = (ctaText = "") => {
  const maxAttempts = 20;
  const attempt = (tries: number) => {
    const element = document.getElementById("room_type");
    if (isPageScrollLocked() || !element) {
      if (tries < maxAttempts) {
        window.setTimeout(() => attempt(tries + 1), 50);
      }
      return;
    }
    // History pop / lock restore can still jump the page one frame later.
    window.setTimeout(() => viewRoomsClickHandler(ctaText), 100);
  };
  window.setTimeout(() => attempt(0), 0);
};

export const getPropertyPageNearbyDataParams = (name: any) => ({
  region_types: "locality,sublocality,establishment",
  id: name,
  for_property_page: true,
  methods: "amber_sales,meta_short",
});

export const addLoadZendeskParamInURL = () => {
  const query = new URLSearchParams(window.location.search);
  query.set("load_zendesk", "true");

  const newURL = `${window.location.origin}${window.location.pathname}?${query.toString()}`;
  window.history.replaceState({ ...window.history.state, path: newURL }, "", newURL);
};

export const filterActiveRoomTypes = (roomTypes: any, limit: number) => {
  const activeRoomTypes: any = [];
  const inactiveRoomTypes: any = [];
  roomTypes.forEach((roomType: any) => {
    if (roomType?.available) {
      activeRoomTypes.push(roomType);
    } else {
      inactiveRoomTypes.push(roomType);
    }
  });
  let finalRoomtypes = [...activeRoomTypes, ...inactiveRoomTypes];

  if (finalRoomtypes.length > limit) {
    finalRoomtypes = finalRoomtypes.slice(0, limit);
  }
  return finalRoomtypes;
};

export const removeLoadZendeskParamFromURL = () => {
  const query = new URLSearchParams(window.location.search);
  const shortlistInv = query.get("load_zendesk");
  if (shortlistInv) {
    query.delete("load_zendesk");
    let newURL = `${window.location.origin}${window.location.pathname}`;
    const qString = query.toString();
    if (qString) {
      newURL = `${newURL}?${qString}`;
    }
    window.history.replaceState({ ...window.history.state, path: newURL }, "", newURL);
  }
};

export function hideUnreadMessagesIframe() {
  const style = document.createElement("style");
  style.textContent = `
              iframe[title="Number of unread messages"] {
                  display: none !important;
              }
`;
  document.head.appendChild(style);
}

export function hideDefaultLauncherButtonIframe() {
  const style = document.createElement("style");
  style.textContent = `
              iframe[title="Button to launch messaging window"],
              iframe[title="Button to launch messaging window, conversation in progress"] {
                  display: none !important;
              }
`;
  document.head.appendChild(style);
}
export function addBottomPaddingtoMessagingWindow() {
  const style = document.createElement("style");
  style.textContent = `
              iframe[title="Messaging window"] {
                  margin-bottom: 12px !important;
              }
`;
  document.head.appendChild(style);
}

export const getUpdatedNavigationData = ({ navigationData, array }: any) =>
  navigationData?.filter((item: any) => {
    const matchingCondition = array.find((condition: any) => condition.id === item.id);
    if (matchingCondition) {
      return matchingCondition.condition;
    }
    return true;
  });

export const mergeAndFilterDuplicateReviews = (reviews: any, reviewsWithMedia: any) => {
  const mergedReviews = [...(reviews || []), ...(reviewsWithMedia || [])];

  const reviewMap = new Map();

  for (const review of mergedReviews) {
    const existingReview = reviewMap.get(review.id);

    if (!existingReview) {
      // First occurrence of this review ID
      reviewMap.set(review.id, review);
    } else {
      // Duplicate found - prefer the one with thumbnail URL
      const currentHasThumbnail = review?.thumbnail;
      const existingHasThumbnail = existingReview?.thumbnail;

      if (currentHasThumbnail && !existingHasThumbnail) {
        // Current review has thumbnail, existing doesn't - prefer current
        reviewMap.set(review.id, review);
      } else if (!currentHasThumbnail && existingHasThumbnail) {
        // Existing review has thumbnail, current doesn't - keep existing
        // Do nothing
      } else {
        // Both have thumbnails or both don't have thumbnails - keep the last one (current behavior)
        reviewMap.set(review.id, review);
      }
    }
  }

  return Array.from(reviewMap.values());
};

export const filterOutWhitelabelInventories = (inventories: any[]): any[] => {
  if (!Array.isArray(inventories)) return [];

  return inventories.filter((inventory) => {
    const isWhitelabel =
      inventory?.parent?.is_whitelabel_inventory || inventory?.is_whitelabel_inventory;
    return !isWhitelabel;
  });
};

export const performRedirect = (redirectUrl: string, staticContext: any): null => {
  staticContext.url = redirectUrl;
  if (__CLIENT__) {
    window.location.href = redirectUrl;
  }
  return null;
};

export const getInventoryWhitelabelPartnerIds = (inventoryData: any): number[] =>
  inventoryData?.whitelabel_partner_ids || inventoryData?.parent?.whitelabel_partner_ids || [];

export const checkWhitelabelInventoryAccess = ({
  isInventoryWhitelabeled,
  isWhitelabelApp,
  partnerIdFromState,
  partnerUuidFromState,
  inventoryData,
  staticContext,
}: {
  isInventoryWhitelabeled: boolean;
  isWhitelabelApp: boolean;
  partnerIdFromState: string | null;
  partnerUuidFromState: string | null;
  inventoryData: any;
  staticContext: any;
}): { shouldRedirect: boolean; redirectResult: any } => {
  if (!isWhitelabelApp) {
    if (isInventoryWhitelabeled) {
      return {
        shouldRedirect: true,
        redirectResult: performRedirect("/", staticContext),
      };
    }
    return { shouldRedirect: false, redirectResult: null };
  }

  if (isWhitelabelApp) {
    if (!isInventoryWhitelabeled) {
      return { shouldRedirect: false, redirectResult: null };
    }

    const inventoryPartnerIds = getInventoryWhitelabelPartnerIds(inventoryData);

    const hasAccess =
      partnerIdFromState &&
      inventoryPartnerIds.some((id: any) => String(id) === String(partnerIdFromState));

    if (hasAccess) {
      return { shouldRedirect: false, redirectResult: null };
    }

    const redirectUrl = partnerUuidFromState ? `/?partner_id=${partnerUuidFromState}` : "/";

    return {
      shouldRedirect: true,
      redirectResult: performRedirect(redirectUrl, staticContext),
    };
  }

  return { shouldRedirect: false, redirectResult: null };
};

export default getPropertyLink;
